import React from "react";
import { graphql } from "gatsby";
import { Layout } from "../components/layout";
import { SEO } from "../components/seo";
import styled from "styled-components";
import { MDXRenderer } from "gatsby-plugin-mdx";
import { TalesList } from "../components/parts";
import { MDXProvider } from "@mdx-js/react";
import { Link } from "../components/link";
import {
  FancyUnderline,
  Code,
} from "../components/common";

const HeroWrapper = styled.div`
  display: block;
  position: relative;
  align-items: center;
  background: ${(props) => props.theme.colors.gray};
  min-height: 748.8px;
  width: 100vw;
  min-height: 60vh;
  background-position: center center;
  background-size: cover;
  position: relative;
  > div {
    padding-top: 10em;
    text-align: center;
    display: block;
    margin: 0 auto;
    line-height: 2.2;
    h1 {
      color: ${(props) => props.theme.colors.text};
      background: ${(props) => props.theme.colors.white};
      font-size: 1.25rem;
      @media (min-width: ${(props) => props.theme.breakpoints.sm}px) {
        font-size: 2rem;
      }
      align-items: center;
      display: inline;
      margin: 0 auto;
      line-height: 1.9;
      box-shadow: 10px 0 0 ${(props) => props.theme.colors.white},
        -10px 0 0 ${(props) => props.theme.colors.white};
    }
    h2 {
      color: ${(props) => props.theme.colors.text};
      background: ${(props) => props.theme.colors.white};
      font-size: 0.8rem;
      @media (min-width: ${(props) => props.theme.breakpoints.sm}px) {
        font-size: 1.25rem;
      }
      align-items: center;
      margin: 0 auto;
      display: inline;
      line-height: 2.2;
      box-shadow: 10px 0 0 ${(props) => props.theme.colors.white},
        -10px 0 0 ${(props) => props.theme.colors.white};
    }
  }
`;

const ContainerReadable = styled.div`
  max-width: ${({ theme }) => theme.other.readable}px!important;
  font-weight: 300;
  p {
    margin: 0 auto 15px auto;
  }
  blockquote {
    padding: 40px 30px 25px;
    position: relative;
    p {
      font-size: 1.25rem;
      @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
        font-size: 2rem;
      }
      line-height: 1.5em;
      font-weight: 700px;
      text-align: center;
    }
    &:before {
      position: absolute;
      top: 0;
      content: "";
      width: 80px;
      height: 80px;
      background-image: url("data:image/svg+xml,%3Csvg aria-hidden='true' focusable='false' data-prefix='fas' data-icon='quote-left' class='svg-inline--fa fa-quote-left fa-w-16' role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='${(props) => props.theme.colors.fade}' d='M464 256h-80v-64c0-35.3 28.7-64 64-64h8c13.3 0 24-10.7 24-24V56c0-13.3-10.7-24-24-24h-8c-88.4 0-160 71.6-160 160v240c0 26.5 21.5 48 48 48h128c26.5 0 48-21.5 48-48V304c0-26.5-21.5-48-48-48zm-288 0H96v-64c0-35.3 28.7-64 64-64h8c13.3 0 24-10.7 24-24V56c0-13.3-10.7-24-24-24h-8C71.6 32 0 103.6 0 192v240c0 26.5 21.5 48 48 48h128c26.5 0 48-21.5 48-48V304c0-26.5-21.5-48-48-48z'%3E%3C/path%3E%3C/svg%3E");
    }
    &:after {
      content: "";
      top: 20px;
      left: 50%;
      margin-left: -100px;
      position: absolute;
      border-bottom: 3px solid ${({ theme }) => theme.colors.cardinal};
      height: 3px;
      width: 200px;
    }
  }
`;

const components = {
  h1: ({ children }) => {
    return <h2>{children}</h2>;
  },
  h2: ({ children }) => {
    return <h2 className="subTitle">{children}</h2>;
  },
  h6: ({ children }) => {
    return <p className="smallText">{children}</p>;
  },
  em: ({ children }) => {
    return <p className="subTitle">{children}</p>;
  },
  pre: Code,
  code: Code,
  inlineCode: Code,
  a: (props) => {
    return <FancyUnderline {...props} to={props.href} />;
  },
};

// type Frontmatter = {
//   title: string,
//   metaImage?: string
//   keywords?: string
//   description?: string
//   subheading?: string
//   slug?: string
//   heading: string
//   hero: string
// }

const tales = ({ data }) => {
  if (data.mdx) {
    const { mdx } = data; // data.mdx holds your post data
    const { frontmatter, body } = mdx;
    return (
      <Layout >
        <SEO
          title={frontmatter.title}
          image={frontmatter.metaImage || frontmatter.hero}
          keywords={frontmatter.keywords}
          description={frontmatter.description}
        />
        <HeroWrapper style={{ backgroundImage: `url(${frontmatter.hero})` }}>
          <div>
            <div>
              {frontmatter.heading ? (
                <h1>{frontmatter.heading}</h1>
              ) : null}
            </div>
            <div>
              {frontmatter.subheading ? (
                <h2>{frontmatter.subheading}</h2>
              ) : null}
            </div>
          </div>
        </HeroWrapper>
        
        <section>
          <ContainerReadable className="container">
            <MDXProvider components={components}>
              <MDXRenderer>{body}</MDXRenderer>
            </MDXProvider>
          </ContainerReadable>
        </section>
        <section>
          <div className="container">
            <TalesList pathName={frontmatter.slug} maxShow={Infinity} />
          </div>
        </section>
      </Layout>
    );
  }
};

export const talesQuery = graphql`
  query($slug: String!) {
    mdx(frontmatter: { slug: { eq: $slug } }) {
      body
      frontmatter {
        slug
        title
        heading
        keywords
        hero
        subheading
      }
    }
  }
`;

export default tales;
